// src/pages/LiveCrewTab.tsx
// ─────────────────────────────────────────────────────────────────
// LIVE CREW TAB — Phase 2/3 implementation
// Includes: crew cards by job, recall system, push delivery status,
// shift awareness, emergency contacts, acknowledgement tracking
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react'
import {
  Users, Phone, AlertTriangle, CheckCircle, Circle, RefreshCw,
  LogOut, MapPin, Clock, Bell, Printer, X, Send, RotateCcw,
  Wifi, Battery, ChevronDown, ChevronUp, UserCheck,
} from 'lucide-react'
import { getOnSiteCrew, ATTENDANCE_META } from '../services/mobileCrewService'
import {
  initiateRecall, acknowledgeRecall, recordReturnToSite,
  resendRecallNotification, getActiveRecalls, getRecallExportHtml,
  getRecallStats, type InitiateRecallInput,
} from '../services/recallService'
import type { CrewMember, RecallEvent, RecallReasonLabel } from '../types/mobileCrewTypes'
import { MOCK_JOBS } from '../utils/mockData'

// ── Recall reason labels ───────────────────────────────────────────
const RECALL_REASONS: Array<{ value: RecallReasonLabel; label: string; icon: string }> = [
  { value: 'WHS incident — active',       label: 'WHS incident / safety stop-work',  icon: '⚠️' },
  { value: 'Site access — revoked',        label: 'Site access revoked',               icon: '🔒' },
  { value: 'Client instruction',           label: 'Client instruction — leave site',   icon: '📋' },
  { value: 'Weather — severe / lightning', label: 'Severe weather / lightning risk',   icon: '⛈️' },
  { value: 'Site emergency',               label: 'Site emergency',                    icon: '🚨' },
  { value: 'End of shift',                 label: 'End of shift — normal departure',   icon: '🕐' },
  { value: 'Other',                        label: 'Other (specify below)',             icon: '📝' },
]

// ── Helpers ────────────────────────────────────────────────────────
function timeAgo(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function StatusPill({ status }: { status: CrewMember['status'] }) {
  const m = ATTENDANCE_META[status as keyof typeof ATTENDANCE_META]
  if (!m) return <span className="text-xs text-gray-400">{status}</span>
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

// ── Recall Modal ───────────────────────────────────────────────────
function RecallModal({
  onClose, onConfirm, scopedJobIds, currentUser,
}: {
  onClose:       () => void
  onConfirm:     (e: RecallEvent) => void
  scopedJobIds:  string[] | null
  currentUser:   any
}) {
  const [reason,      setReason]      = useState<RecallReasonLabel | ''>('')
  const [freeText,    setFreeText]    = useState('')
  const [musterPoint, setMusterPoint] = useState('')
  const [returnETA,   setReturnETA]   = useState('')
  const [urgency,     setUrgency]     = useState<'IMMEDIATE' | 'END_OF_TASK' | 'END_OF_SHIFT'>('IMMEDIATE')
  const [selectedJobs,setSelectedJobs]= useState<string[]>(
    scopedJobIds ?? MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status)).map(j => j.id)
  )
  const [submitting,  setSubmitting]  = useState(false)

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))

  const toggleJob = (jId: string) => setSelectedJobs(prev =>
    prev.includes(jId) ? prev.filter(x => x !== jId) : [...prev, jId]
  )

  const handleConfirm = async () => {
    if (!reason || selectedJobs.length === 0) return
    setSubmitting(true)
    try {
      const crewOnJobs = await getOnSiteCrew()
      const affected = crewOnJobs
        .filter(c => selectedJobs.includes(c.jobId))
        .map(c => ({
          employeeId: c.employeeId,
          employeeName: c.employeeName,
          role: c.role,
          currentStatus: c.status,
        }))
      const input: InitiateRecallInput = {
        jobIds:        selectedJobs,
        jobNumbers:    activeJobs.filter(j => selectedJobs.includes(j.id)).map(j => j.jobNumber),
        reason:        reason as RecallReasonLabel,
        freeText:      freeText || null,
        musterPoint:   musterPoint || null,
        returnETA:     returnETA || null,
        urgency,
        affectedCrew:  affected,
        issuedById:    currentUser?.id   ?? 'u_pm',
        issuedByName:  currentUser?.fullName ?? 'PM',
        issuedByTier:  currentUser?.tier ?? 'B_PM',
      }
      const event = await initiateRecall(input)
      onConfirm(event)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-red-600 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <LogOut className="w-5 h-5 text-white" />
            <h2 className="text-base font-bold text-white">Initiate Site Recall</h2>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-white/70 hover:text-white" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Urgency */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Urgency *</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { v:'IMMEDIATE',    label:'Immediate',       sub:'Leave site now',        col:'red'   },
                { v:'END_OF_TASK',  label:'End of task',     sub:'Complete then depart',  col:'amber' },
                { v:'END_OF_SHIFT', label:'End of shift',    sub:'Normal shift end',      col:'green' },
              ] as const).map(u => (
                <button key={u.v}
                  type="button"
                  onClick={() => setUrgency(u.v)}
                  className={`p-2.5 rounded-xl border-2 text-left transition-colors ${
                    urgency === u.v
                      ? u.col === 'red'   ? 'border-red-500 bg-red-50'
                      : u.col === 'amber' ? 'border-amber-500 bg-amber-50'
                                          : 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className="text-xs font-bold text-gray-900">{u.label}</div>
                  <div className="text-xs text-gray-500">{u.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">Reason *</label>
            <div className="space-y-1.5">
              {RECALL_REASONS.map(r => (
                <button key={r.value} type="button"
                  onClick={() => setReason(r.value)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left text-sm transition-colors ${
                    reason === r.value ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}>
                  <span className="text-base">{r.icon}</span>
                  <span className="font-medium">{r.label}</span>
                  {reason === r.value && <CheckCircle className="w-4 h-4 text-blue-600 ml-auto flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* Free text */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Additional detail</label>
            <textarea
              value={freeText}
              onChange={e => setFreeText(e.target.value)}
              rows={2}
              placeholder="Specific instructions or context for this recall..."
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
            />
          </div>

          {/* Muster point */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Muster point</label>
              <input
                value={musterPoint}
                onChange={e => setMusterPoint(e.target.value)}
                placeholder="e.g. Site gate, car park"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Expected return (optional)</label>
              <input
                value={returnETA}
                onChange={e => setReturnETA(e.target.value)}
                placeholder="e.g. Tomorrow 7am"
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Job selector */}
          {activeJobs.length > 1 && (
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Affected jobs *</label>
              <div className="flex flex-wrap gap-2">
                {activeJobs.map(j => (
                  <button key={j.id} type="button"
                    onClick={() => toggleJob(j.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-colors ${
                      selectedJobs.includes(j.id)
                        ? 'border-blue-500 bg-blue-50 text-blue-800'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}>
                    {j.jobNumber}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t bg-gray-50 rounded-b-2xl flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!reason || selectedJobs.length === 0 || submitting}
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Initiating...</>
              : <><LogOut className="w-4 h-4" /> Initiate Recall</>
            }
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Active Recall Status Panel ─────────────────────────────────────
function RecallStatusPanel({
  recall, onAck, onReturn, onResend, onExport,
}: {
  recall:   RecallEvent
  onAck:    (empId: string) => void
  onReturn: (empId: string) => void
  onResend: () => void
  onExport: () => void
}) {
  const stats = getRecallStats(recall)
  const ackPct = stats.total > 0 ? Math.round((stats.acked / stats.total) * 100) : 0

  return (
    <div className="bg-red-600 rounded-2xl overflow-hidden shadow-lg">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
          <div>
            <p className="text-white font-bold text-sm">SITE RECALL ACTIVE — {recall.id}</p>
            <p className="text-red-200 text-xs mt-0.5">{recall.reason} · {recall.urgency.replace(/_/g,' ')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onResend}
            className="px-3 py-1.5 rounded-lg bg-white/20 text-white text-xs font-medium hover:bg-white/30 flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" />Resend
          </button>
          <button onClick={onExport}
            className="px-3 py-1.5 rounded-lg bg-white/20 text-white text-xs font-medium hover:bg-white/30 flex items-center gap-1.5">
            <Printer className="w-3.5 h-3.5" />Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 pb-3 grid grid-cols-4 gap-3">
        {[
          { label:'Total',        value:stats.total,   col:'text-white' },
          { label:'Acknowledged', value:stats.acked,   col:'text-green-300' },
          { label:'Pending',      value:stats.pending, col:'text-red-200' },
          { label:'Returned',     value:stats.returned,col:'text-blue-300' },
        ].map(s => (
          <div key={s.label} className="bg-white/10 rounded-xl px-3 py-2 text-center">
            <div className={`text-xl font-bold ${s.col}`}>{s.value}</div>
            <div className="text-white/60 text-xs mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Ack progress bar */}
      <div className="px-5 pb-3">
        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-400 rounded-full transition-all"
            style={{ width:`${ackPct}%` }}
          />
        </div>
        <p className="text-xs text-red-200 mt-1">{ackPct}% acknowledged</p>
      </div>

      {/* Per-crew status */}
      <div className="bg-white/5 border-t border-white/10 divide-y divide-white/10">
        {recall.affectedCrew.map(c => (
          <div key={c.employeeId} className="flex items-center gap-3 px-5 py-2.5">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
              c.returnedToSiteAt ? 'bg-blue-400' : c.acknowledgedAt ? 'bg-green-400' : 'bg-red-300 animate-pulse'
            }`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-medium">{c.employeeName}</span>
                <span className="text-white/50 text-xs">{c.role}</span>
              </div>
              <div className="text-xs mt-0.5">
                {c.returnedToSiteAt
                  ? <span className="text-blue-300">✓ Returned {timeAgo(c.returnedToSiteAt)}</span>
                  : c.acknowledgedAt
                  ? <span className="text-green-300">✓ Acknowledged {timeAgo(c.acknowledgedAt)}</span>
                  : <span className="text-red-300">⏳ Recall sent — awaiting acknowledgement</span>
                }
              </div>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              {!c.acknowledgedAt && (
                <button onClick={() => onAck(c.employeeId)}
                  className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded-lg font-medium">
                  Ack
                </button>
              )}
              {c.acknowledgedAt && !c.returnedToSiteAt && (
                <button onClick={() => onReturn(c.employeeId)}
                  className="text-xs bg-blue-400 hover:bg-blue-500 text-white px-2 py-1 rounded-lg font-medium">
                  Returned
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {recall.musterPoint && (
        <div className="px-5 py-3 border-t border-white/10 flex items-center gap-2 text-sm">
          <MapPin className="w-4 h-4 text-red-200 flex-shrink-0" />
          <span className="text-white"><strong>Muster:</strong> {recall.musterPoint}</span>
        </div>
      )}
      {recall.freeText && (
        <div className="px-5 py-2 text-xs text-red-100 bg-black/10">
          {recall.freeText}
        </div>
      )}
    </div>
  )
}

// ── Crew Card ──────────────────────────────────────────────────────
function CrewCard({ crew, recall }: { crew: CrewMember; recall: RecallEvent | null }) {
  const [expanded, setExpanded] = useState(false)
  const recallStatus = recall?.affectedCrew.find(c => c.employeeId === crew.employeeId)

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${
      recallStatus && !recallStatus.acknowledgedAt ? 'border-red-300 bg-red-50/30' :
      crew.status === 'REJECTED' ? 'border-red-200 bg-red-50/20' : 'border-gray-100 bg-white'
    }`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50"
        onClick={() => setExpanded(v => !v)}
      >
        {/* Status dot */}
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
          ATTENDANCE_META[crew.status as keyof typeof ATTENDANCE_META]?.dot ?? 'bg-gray-300'
        }`} />

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{crew.employeeName}</span>
            <span className="text-xs text-gray-400">{crew.role}</span>
            <StatusPill status={crew.status} />
            {recallStatus && !recallStatus.acknowledgedAt && (
              <span className="text-xs bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">
                🔴 Recall pending
              </span>
            )}
          </div>
          {crew.status === 'ON_SITE' && crew.startTime && (
            <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <Clock className="w-3 h-3" />Started {crew.startTime}
            </div>
          )}
          {crew.status === 'REJECTED' && (
            <div className="text-xs text-red-600 mt-0.5">
              Rejected — {crew.rejectionNote ?? 'no reason given'}
            </div>
          )}
        </div>

        {/* Shift badge */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {crew.shiftType === 'NIGHT'
            ? <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><span>🌙</span>Night</span>
            : <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><span>☀️</span>Day</span>
          }
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-300" /> : <ChevronDown className="w-4 h-4 text-gray-300" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-2 bg-gray-50/50">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <p className="text-gray-500">Job</p>
              <p className="font-medium text-gray-900">{crew.jobNumber} · {crew.siteAddress}</p>
            </div>
            <div>
              <p className="text-gray-500">Last sync</p>
              <p className="font-medium text-gray-900 flex items-center gap-1">
                <Wifi className="w-3 h-3 text-green-500" />
                {crew.lastSyncTimestamp ? timeAgo(crew.lastSyncTimestamp) : 'Unknown'}
              </p>
            </div>
            {crew.emergencyContact && (
              <div>
                <p className="text-gray-500">Emergency contact</p>
                <a href={`tel:${crew.emergencyContact.phone}`}
                  className="font-medium text-blue-600 hover:underline flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {crew.emergencyContact.name} ({crew.emergencyContact.relationship}) — {crew.emergencyContact.phone}
                </a>
              </div>
            )}
            {crew.supervisorName && (
              <div>
                <p className="text-gray-500">Supervisor</p>
                <p className="font-medium text-gray-900">{crew.supervisorName}</p>
              </div>
            )}
          </div>
          {crew.notes && (
            <p className="text-xs text-gray-500 italic">"{crew.notes}"</p>
          )}
          {crew.unreadNotificationCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 rounded-lg px-2 py-1">
              <Bell className="w-3 h-3" />
              {crew.unreadNotificationCount} unread notification{crew.unreadNotificationCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main LiveCrewTab ───────────────────────────────────────────────
export function LiveCrewTab({ scopedJobIds, currentUser }: {
  scopedJobIds: string[] | null
  currentUser:  any
}) {
  const [crew,          setCrew]          = useState<CrewMember[]>([])
  const [activeRecall,  setActiveRecall]  = useState<RecallEvent | null>(null)
  const [loading,       setLoading]       = useState(true)
  const [showRecall,    setShowRecall]    = useState(false)
  const [jobFilter,     setJobFilter]     = useState('ALL')

  const load = useCallback(async () => {
    const [c, recalls] = await Promise.all([getOnSiteCrew(), getActiveRecalls()])
    setCrew(c)
    setActiveRecall(recalls[0] ?? null)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const visibleJobs = MOCK_JOBS.filter(j =>
    !['COMPLETED','CLOSED'].includes(j.status) &&
    (scopedJobIds === null || scopedJobIds.includes(j.id))
  )

  const filteredCrew = crew.filter(c =>
    jobFilter === 'ALL' || c.jobId === jobFilter
  )

  const byJob = visibleJobs.reduce((acc, job) => {
    acc[job.id] = filteredCrew.filter(c => c.jobId === job.id)
    return acc
  }, {} as Record<string, CrewMember[]>)

  const rejectedCrew = filteredCrew.filter(c => c.status === 'REJECTED')

  async function handleRecallInitiated(event: RecallEvent) {
    setShowRecall(false)
    setActiveRecall(event)
    await load()
  }

  async function handleAck(empId: string) {
    if (!activeRecall) return
    await acknowledgeRecall(activeRecall.id, empId)
    await load()
  }

  async function handleReturn(empId: string) {
    if (!activeRecall) return
    await recordReturnToSite(activeRecall.id, empId)
    await load()
  }

  async function handleResend() {
    if (!activeRecall) return
    const count = await resendRecallNotification(activeRecall.id)
    alert(`Recall reminder sent to ${count} pending crew member${count !== 1 ? 's' : ''}.`)
  }

  async function handleExportRecall() {
    if (!activeRecall) return
    const html = await getRecallExportHtml(activeRecall.id)
    if (!html) return
    const w = window.open('', '_blank')
    if (w) { w.document.write(html); w.document.title = activeRecall.id; w.print() }
  }

  if (loading) return (
    <div className="py-16 flex items-center justify-center text-gray-400">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading crew...
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Active recall panel */}
      {activeRecall && (
        <RecallStatusPanel
          recall={activeRecall}
          onAck={handleAck}
          onReturn={handleReturn}
          onResend={handleResend}
          onExport={handleExportRecall}
        />
      )}

      {/* Header row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">
            {filteredCrew.length} crew across {visibleJobs.filter(j => (byJob[j.id]?.length ?? 0) > 0).length} job{visibleJobs.filter(j => (byJob[j.id]?.length ?? 0) > 0).length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
            className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" />
          </button>
          {!activeRecall && (
            <button
              onClick={() => setShowRecall(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700">
              <LogOut className="w-4 h-4" />Recall all crew
            </button>
          )}
        </div>
      </div>

      {/* Rejection alert bar */}
      {rejectedCrew.length > 0 && !activeRecall && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-red-700">
            {rejectedCrew.length} attendance rejection{rejectedCrew.length !== 1 ? 's' : ''} require action
          </span>
        </div>
      )}

      {/* Job filter pills */}
      {visibleJobs.length > 1 && (
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setJobFilter('ALL')}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
              jobFilter === 'ALL' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            All jobs
          </button>
          {visibleJobs.map(j => (
            <button key={j.id}
              onClick={() => setJobFilter(j.id)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                jobFilter === j.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {j.jobNumber}
              {byJob[j.id]?.filter(c => c.status === 'REJECTED').length > 0 && (
                <span className="ml-1 text-red-600">⚠</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Per-job crew sections */}
      {visibleJobs.filter(j => jobFilter === 'ALL' || j.id === jobFilter).map(job => {
        const jobCrew = byJob[job.id] ?? []
        if (jobCrew.length === 0) return null

        const onSite    = jobCrew.filter(c => c.status === 'ON_SITE').length
        const confirmed = jobCrew.filter(c => c.status === 'CONFIRMED').length
        const pending   = jobCrew.filter(c => c.status === 'PENDING').length
        const rejected  = jobCrew.filter(c => c.status === 'REJECTED').length
        const night     = jobCrew.filter(c => c.shiftType === 'NIGHT').length
        const day       = jobCrew.filter(c => c.shiftType === 'DAY').length

        return (
          <div key={job.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* Job header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 text-sm">{job.jobNumber}</span>
                  <span className="text-xs text-gray-500">
                    {(job as any).description?.split('—')[0]?.trim()}
                  </span>
                </div>
                <div className="flex gap-3 mt-0.5 text-xs flex-wrap">
                  {onSite > 0    && <span className="text-blue-700 font-medium">{onSite} on site</span>}
                  {confirmed > 0 && <span className="text-green-700 font-medium">{confirmed} confirmed</span>}
                  {pending > 0   && <span className="text-amber-700">{pending} pending</span>}
                  {rejected > 0  && <span className="text-red-700 font-bold">{rejected} rejected ⚠</span>}
                  {night > 0 && day > 0 && <span className="text-gray-400">🌙{night} day / ☀️{day} night</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-gray-500">Live</span>
              </div>
            </div>

            {/* Crew cards */}
            <div className="p-3 space-y-2">
              {jobCrew.map(c => (
                <CrewCard key={c.id} crew={c} recall={activeRecall} />
              ))}
            </div>
          </div>
        )
      })}

      {filteredCrew.length === 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <UserCheck className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No crew on site</p>
          <p className="text-gray-300 text-sm mt-1">Push a roster to see crew appear here</p>
        </div>
      )}

      {/* Recall modal */}
      {showRecall && (
        <RecallModal
          onClose={() => setShowRecall(false)}
          onConfirm={handleRecallInitiated}
          scopedJobIds={scopedJobIds}
          currentUser={currentUser}
        />
      )}
    </div>
  )
}
